import React from 'react';
import { motion } from 'framer-motion';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ShoppingCart, CheckCircle2, X, ChevronRight } from 'lucide-react';
import { MedicationCardProps } from './medication-types';

const MedicationCard: React.FC<MedicationCardProps> = ({
  medication,
  onClick,
  isDefault = false,
  isSelected = false,
  showPrice = true,
  onRemove,
  mode = 'prescription',
  selectedMedication,
}) => {
  const availableDoses =
    medication.doses?.filter((dose: string) => dose && dose.trim() !== '') || [];

  // Get the first dose's price for display
  const productIndex = medication.map_dose_to_product?.findIndex(product => product.price > 0);
  const displayPrice = selectedMedication
    ? selectedMedication.price
    : parseFloat(medication.map_dose_to_product?.[productIndex]?.price?.toString?.() || '0');

  if (!medication.name) {
    return null;
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      onClick={() => onClick(medication)}
      className='group cursor-pointer'
    >
      <div
        className={`relative h-full overflow-hidden rounded-xl border bg-white transition-all duration-300 ${
          isSelected
            ? 'border-emerald-400 shadow-lg shadow-emerald-100'
            : 'border-slate-200 hover:border-slate-300 hover:shadow-xl'
        }`}
      >
        {/* Product Image Area */}
        <div className='relative aspect-[4/3] overflow-hidden bg-gradient-to-br from-slate-100 to-slate-50'>
          {medication.image_url ? (
            <img
              src={medication.image_url}
              alt={medication.name}
              className='h-full w-full object-contain transition-transform duration-500 group-hover:scale-110'
            />
          ) : (
            <div className='flex h-full w-full items-center justify-center'>
              <div className='text-center'>
                <svg
                  className='h-20 w-20 text-slate-300 transition-transform duration-300 group-hover:scale-110'
                  viewBox='0 0 40 40'
                  fill='currentColor'
                >
                  <path d='M5.667,31.843C5.3,31.843,5,31.544,5,31.175V8.823c0-0.367,0.3-0.666,0.667-0.666h28.666C34.7,8.157,35,8.456,35,8.823 v22.352c0,0.369-0.3,0.668-0.667,0.668H5.667z M7.044,30.507h25.734l-8.068-8.582l-3.695,3.928 c-0.127,0.135-0.303,0.207-0.494,0.207c-0.225-0.008-0.404-0.105-0.524-0.262l-4.704-6.205L7.044,30.507z M24.71,20.282 c0.182,0,0.359,0.076,0.485,0.209l8.469,9.008V9.493H6.336v19.734l8.421-11.143c0.127-0.166,0.326-0.268,0.533-0.268 c0.213,0,0.406,0.098,0.533,0.266l4.759,6.275l3.639-3.867C24.349,20.358,24.526,20.282,24.71,20.282L24.71,20.282z' />
                  <path d='M25.154,17.769c-1.616,0-2.931-1.316-2.931-2.932s1.314-2.93,2.931-2.93c1.617,0,2.932,1.314,2.932,2.93 S26.771,17.769,25.154,17.769L25.154,17.769z M25.154,13.243c-0.879,0-1.595,0.715-1.595,1.594s0.716,1.596,1.595,1.596 c0.88,0,1.596-0.717,1.596-1.596S26.034,13.243,25.154,13.243L25.154,13.243z' />
                </svg>
              </div>
            </div>
          )}

          {/* Badges Overlay */}
          {isDefault && !isSelected && (
            <div className='absolute left-3 top-3'>
              <Badge className='bg-amber-500 px-3 py-1 text-xs font-semibold text-white shadow-lg'>
                Recommended
              </Badge>
            </div>
          )}

          {/* Selected/In Cart Badge - Top Right */}
          {isSelected && (
            <div className='absolute right-3 top-3 flex items-center gap-2'>
              {onRemove && (
                <Button
                  size='icon'
                  variant='ghost'
                  className='h-6 w-6 rounded-full bg-red-500 p-0 text-white shadow-lg hover:bg-red-600 hover:text-white'
                  onClick={e => {
                    e.stopPropagation();
                    onRemove(medication);
                  }}
                >
                  <X className='h-4 w-4' />
                </Button>
              )}
              <Badge className='bg-emerald-500 px-3 py-1 text-xs font-semibold text-white shadow-lg'>
                {mode === 'cart' ? (
                  <>
                    <ShoppingCart className='mr-1.5 h-3 w-3' />
                    In Cart
                  </>
                ) : (
                  <>
                    <CheckCircle2 className='mr-1.5 h-3 w-3' />
                    Selected
                  </>
                )}
              </Badge>
            </div>
          )}

          {/* Quick Add Overlay on Hover */}
          <div className='absolute inset-x-0 bottom-0 translate-y-full bg-gradient-to-t from-black/80 to-transparent p-4 transition-transform duration-300 group-hover:translate-y-0'>
            <Button className='w-full rounded-xl bg-white py-3 font-semibold text-slate-900 shadow-xl hover:bg-slate-100'>
              {isSelected ? 'Edit Selection' : 'Configure & Add'}
            </Button>
          </div>
        </div>

        {/* Product Info */}
        <div className='p-4'>
          <h3 className='mb-2 line-clamp-2 text-sm font-semibold leading-tight text-slate-900 transition-colors group-hover:text-emerald-600'>
            {medication.name}
          </h3>

          {/* Variants Info */}
          {availableDoses.length > 1 && (
            <div className='mb-3 flex flex-wrap gap-2'>
              <span className='inline-flex items-center rounded-full bg-slate-100 px-2.5 py-0.5 text-[11px] font-medium text-slate-600'>
                {availableDoses.length} doses available
              </span>
            </div>
          )}

          {/* Price */}
          {showPrice && (
            <div className='flex items-end justify-between'>
              <div>
                <p className='text-[10px] font-medium uppercase tracking-wider text-slate-400'>
                  {availableDoses.length === 1 ? 'Starting At' : 'Starting At'}
                </p>
                <p className='text-lg font-bold text-slate-900'>
                  ${displayPrice > 0 ? displayPrice.toFixed(2) : '0.00'}
                </p>
              </div>
              <div className='flex h-8 w-8 items-center justify-center rounded-full bg-emerald-500 text-white opacity-0 shadow-lg transition-all duration-300 group-hover:opacity-100'>
                <ChevronRight className='h-4 w-4' />
              </div>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default MedicationCard;
